import { useMemo, useRef, useState } from 'react';
import { Button, Form, Tab, Tabs } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import spinnerTitle from 'components/others/spinnerTitle';
import SvgSave from 'components/svg/SvgSave';
import type { ProductType } from '@econtractor/types';
import ProposalGeneralTab, { type ProposalGeneralTabHandle } from '../share/ProposalGeneralTab';
import ProposalContentTab from './tabs/ProposalContentTab';
import ProposalPreviewTab from '../share/ProposalPreviewTab';
import ProposalGenerateTab from './tabs/ProposalGenerateTab';
import { TemplateProducts, TemplateAdditionalItems } from '../share'
import type { ProposalEditFormValues } from './types';
import { resolveProposalName } from '../share/model/helper/proposalEditorUtils';
import {
    formatProposalTotal,
    getProposalTotalAmount,
    getProposalUseUsd,
} from '../share/model/helper/proposalPriceUtils';
import { getBaseCurrencyCode, getCurrencyCode } from 'utils/helpers/currency/currencyHelpers';
import SolarIcon from 'components/icons/SolarIcon';

interface ProposalEditorFormProps {
    mode: 'dir' | 'lead';
    leadId?: number | string;
    leadProposalId?: number | string;
    leadEmail?: string;
    leadName?: string;
    leadStatus?: number;
    /** Lead/client record for template key substitution in preview/PDF. */
    objectData?: unknown;
    initialValues: ProposalEditFormValues;
    loading?: boolean;
    submitLabel: string;
    productsMap: Record<number, ProductType>;
    rate: number;
    onSubmit: (payload: ProposalEditFormValues) => Promise<void>;
    onGenerated?: () => Promise<void> | void;
}

const ProposalEditorForm = ({
    mode,
    leadId,
    leadProposalId,
    leadEmail = '',
    leadName = '',
    leadStatus,
    objectData,
    initialValues,
    loading = false,
    submitLabel,
    productsMap,
    rate,
    onSubmit,
    onGenerated,
}: ProposalEditorFormProps) => {
    const isBackgroundFieldVisible = false;
    const { auth } = useAuth();
    const { t } = useTranslation(['Proposal', 'common']);
    const { showToast } = useToast();
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const generalTabRef = useRef<ProposalGeneralTabHandle>(null);
    const [isImageBusy, setIsImageBusy] = useState(false);
    const [formData, setFormData] = useState<ProposalEditFormValues>(initialValues);
    const useUsd = getProposalUseUsd(formData.pricesInUsd, rate);
    const totalAmount = useMemo(() => getProposalTotalAmount(formData, rate), [formData, rate]);
    const isSaving = loading || isImageBusy;

    const handleChange = (patch: Partial<ProposalEditFormValues>) => {
        setFormData((prev) => ({ ...prev, ...patch }));
    };

    return (
        <Form
            className="off-proposal-form d-flex flex-column h-100"
            noValidate
            onSubmit={async (e) => {
                e.preventDefault();
                if (!String(formData.proposalName || '').trim()) {
                    showToast(t('edit.fields.proposalNameRequired'), 'warning');
                    return;
                }
                const { proposalLogo, proposalBackground } =
                    (await generalTabRef.current?.uploadPendingImages()) || {
                        proposalLogo: formData.proposalLogo,
                        proposalBackground: formData.proposalBackground,
                    };
                const submitData: ProposalEditFormValues = {
                    ...formData,
                    proposalLogo,
                    proposalBackground,
                    proposalName: resolveProposalName(
                        mode,
                        formData.proposalName,
                        leadName,
                        t('edit.fields.proposalName'),
                    ),
                };

                // Keep current preview blocks in sync without reopening offcanvas.
                setFormData(submitData);
                await onSubmit(submitData);
            }}
        >
            <div className="d-flex flex-column h-100">
                <div className="off-proposal-content px-3">
                    <Tabs variant="underline" defaultActiveKey="general" className="mb-3 invoice-tab border-bottom">
                        <Tab eventKey="general" title={<><SolarIcon name="gear" className="me-2 fs-14" />{t('edit.tabGeneral')}</>}>
                            <ProposalGeneralTab
                                ref={generalTabRef}
                                mode={mode}
                                formData={formData}
                                rate={rate}
                                loading={loading}
                                disabled={!canEdit}
                                isBackgroundFieldVisible={isBackgroundFieldVisible}
                                onChange={handleChange}
                                onBusyChange={setIsImageBusy}/>
                        </Tab>

                        <Tab eventKey="content" title={<><SolarIcon name="card-text" className="me-2 fs-14" />{t('edit.tabContent')}</>}>
                            <ProposalContentTab
                                formData={formData}
                                disabled={!canEdit}
                                objectData={objectData}
                                onChange={handleChange}
                            />
                        </Tab>

                        <Tab eventKey="products" title={<><SolarIcon name="box-seam" className="me-2 fs-14" />{t('edit.tabProducts')}</>}>
                            <TemplateProducts
                                products={formData.products}
                                productsMap={productsMap}
                                rate={rate}
                                disabled={!canEdit}
                                onChange={(products) => handleChange({ products })}
                            />
                        </Tab>

                        <Tab eventKey="additional" title={<><SolarIcon name="list-ul" className="me-2 fs-14" />{t('edit.tabAdditional')}</>}>
                            <TemplateAdditionalItems
                                items={formData.additionalItems}
                                rate={rate}
                                disabled={!canEdit}
                                onChange={(additionalItems) => handleChange({ additionalItems })}
                            />
                        </Tab>

                        <Tab eventKey="preview" title={<><SolarIcon name="file-earmark-richtext" className="me-2 fs-14" />{t('edit.tabPreview')}</>}>
                            <ProposalPreviewTab
                                formData={formData}
                                rate={rate}
                                showTitlePage={Boolean(formData.proposalTitlePage)}
                                objectData={objectData}
                            />
                        </Tab>

                        {mode === 'lead' ? (
                            <Tab eventKey="generate" title={<><SolarIcon name="file-earmark-arrow-down" className="me-2 fs-14" />{t('edit.tabGenerate')}</>}>
                                <ProposalGenerateTab
                                    leadId={leadId}
                                    leadProposalId={leadProposalId}
                                    leadEmail={leadEmail}
                                    leadStatus={leadStatus}
                                    formData={formData}
                                    rate={rate}
                                    objectData={objectData}
                                    onGenerated={onGenerated}/>
                            </Tab>
                        ) : null}
                    </Tabs>
                </div>

                <div className="p-3 offcanvas-footer off-proposal-footer mt-auto">
                    {canEdit ? (
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="proposal-footer-total">
                                <div className="proposal-footer-total-label">{t('edit.totalCostLabel')}</div>
                                <div className="proposal-footer-total-value">
                                    {formatProposalTotal(totalAmount, rate, formData.pricesInUsd)}
                                    <span className="badge bg-light-secondary text-secondary ms-2">
                                        {useUsd ? getBaseCurrencyCode() : getCurrencyCode()}
                                    </span>
                                    {formData.proposalVat ? (
                                        <span className="badge bg-light-info text-info ms-1">
                                            {t('edit.general.vatLabel')}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            <Button
                                type="submit"
                                variant="info"
                                disabled={isSaving}
                                className="min100px"
                            >
                                {spinnerTitle(
                                    <>
                                        <SvgSave className="me-2 mtop-2" />
                                        {submitLabel}
                                    </>,
                                    isSaving,
                                    t('common:buttons.saving'),
                                )}
                            </Button>
                        </div>
                    ) : (
                        <div className="alert alert-warning text-start mb-0" role="alert">
                            {t('common:messReadOnly')}
                        </div>
                    )}
                </div>
            </div>
        </Form>
    );
};

export default ProposalEditorForm;
